import { useState, useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const baseItineraries = [
  {
    slug: "goa",
    title: "Goa Beach Escape",
    duration: "5 days",
    location: "Goa, India",
    image: "https://images.unsplash.com/photo-1614082242765-7c98ca0f3df3?w=1000&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8Z29hfGVufDB8fDB8fHww",
  },
  {
    slug: "sikkim-gangtok",
    title: "Sikkim Explorer",
    duration: "6 days",
    location: "Sikkim, India",
    image: "https://images.unsplash.com/photo-1634400001131-d04275db2076?w=1000&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTB8fHNpa2tpbXxlbnwwfHwwfHx8MA%3D%3D",
  },
  {
    slug: "rajastan-royal",
    title: "Jaipur Heritage",
    duration: "3 days",
    location: "Rajasthan",
    image: "https://images.unsplash.com/photo-1619014452346-dc868be175cb?w=1000&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8cmFqc3RoYW58ZW58MHx8MHx8fDA%3D",
  },
  {
    slug: "kerala-backwater",
    title: "Kerala Backwaters",
    duration: "4 days",
    location: "Kerala",
    image: "https://plus.unsplash.com/premium_photo-1697729600773-5b039ef17f3b?w=1000&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8a2VyYWxhfGVufDB8fDB8fHww",
  },
];

// Duplicate to ensure infinite carousel wrap-around doesn't glitch visually
const itineraries = [...baseItineraries, ...baseItineraries.map(i => ({...i, slug: i.slug + '-dup'}))];

const ItinerarySlider = ({ onItineraryClick = () => { } }) => {
  const [index, setIndex] = useState(0);
  const total = itineraries.length;

  // 🔹 Swipe state
  const [startX, setStartX] = useState(null);
  const [currentX, setCurrentX] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [cardWidth, setCardWidth] = useState(380);
  const draggedRef = useRef(false);

  useEffect(() => {
    const updateWidth = () => {
      setCardWidth(window.innerWidth < 768 ? 300 : 400);
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const prev = useCallback(() => {
    setIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  const next = useCallback(() => {
    setIndex((prev) => (prev + 1) % total);
  }, [total]);

  // 🔹 Touch/Mouse handlers
  const handleDragStart = (e) => {
    const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    setStartX(clientX);
    setCurrentX(clientX);
    setIsDragging(true);
    draggedRef.current = false;
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    setCurrentX(clientX);
    if (Math.abs(clientX - startX) > 10) {
      draggedRef.current = true;
    }
  };

  const handleDragEnd = () => {
    if (!isDragging || startX === null || currentX === null) {
      setIsDragging(false);
      setStartX(null);
      setCurrentX(null);
      return;
    }

    const diff = startX - currentX;
    const threshold = 50;

    if (diff > threshold) next();
    else if (diff < -threshold) prev();

    setIsDragging(false);
    setStartX(null);
    setCurrentX(null);
    
    setTimeout(() => { draggedRef.current = false; }, 50);
  };

  const handleLinkClick = (e) => {
    if (draggedRef.current) e.preventDefault();
  };

  const dragOffset = isDragging && currentX !== null && startX !== null ? currentX - startX : 0;

  return (
    <section className="w-full py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        {/* HEADING */}
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-3xl md:text-5xl font-serif text-[#1f2d1f]">
            Places You’ll Brag About Forever
          </h2>
          <p className="mt-4 text-[#6b7280] max-w-2xl mx-auto text-base md:text-lg px-4">
            Destinations so good, they turn into stories, memories,
            and serious travel envy.
          </p>
        </div>

        {/* SLIDER */}
        <div className="relative w-full max-w-6xl mx-auto h-[480px] md:h-[540px]">
          <div
            className="overflow-hidden h-full touch-pan-y select-none"
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              {itineraries.map((item, i) => {
                let offset = i - index;
                
                // Handle infinite wrap-around
                if (offset > total / 2) offset -= total;
                if (offset < -total / 2) offset += total;

                // Render wider range to prevent popping
                if (Math.abs(offset) > 2) return null;

                const isCenter = offset === 0;
                const absOffset = Math.abs(offset);

                // Damped drag for premium physics feel
                const dampedDrag = dragOffset * 0.85;
                const baseTranslate = offset * cardWidth;
                const translateX = baseTranslate + dampedDrag;

                // Stable dimensions, pure opacity for depth
                const scale = isCenter ? 1 : absOffset === 1 ? 0.98 : 0.94;
                const opacity = isCenter ? 1 : absOffset === 1 ? 0.6 : 0;
                const zIndex = 20 - absOffset;

                // CSS Transitions - disable during drag
                const transitionStyle = isDragging
                  ? "none"
                  : "transform 0.8s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1)";

                return (
                  <div
                    key={item.slug + '-' + i}
                    className="absolute flex justify-center items-center cursor-grab active:cursor-grabbing"
                    style={{
                      transform: `translate3d(${translateX}px, 0, 0) scale(${scale})`,
                      opacity,
                      zIndex,
                      transition: transitionStyle,
                      willChange: "transform, opacity",
                      backfaceVisibility: "hidden",
                      pointerEvents: isCenter ? "auto" : "none",
                    }}
                  >
                    <div
                      className="relative w-[280px] sm:w-[380px] h-[440px] md:h-[500px] rounded-[24px] md:rounded-[32px] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.12)] bg-[#111] group"
                    >
                      {/* IMAGE (Linked) */}
                      <Link to={`/itineraries/${item.slug.replace('-dup', '')}`} onClick={handleLinkClick} className="absolute inset-0 z-0 select-none">
                        <img
                          src={item.image}
                          alt={`${item.title} - ${item.duration} travel plan`}
                          loading="lazy"
                          draggable="false"
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
                      </Link>

                      {/* TAGS */}
                      <div className="absolute top-6 left-6 flex gap-2 z-10 pointer-events-none">
                        <span className="bg-white/20 backdrop-blur-md text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border border-white/20 shadow-sm">
                          Top Rated
                        </span>
                      </div>

                      {/* TEXT */}
                      <div className="absolute bottom-8 left-8 right-8 text-white z-10 pointer-events-none">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-white/80 mb-2 font-bold drop-shadow-md">{item.location}</div>
                        <h3 className="text-xl md:text-2xl font-semibold leading-tight drop-shadow-md">
                          {item.title}
                        </h3>
                        <p className="text-sm text-white/90 mt-2 mb-4 flex items-center gap-2 drop-shadow-sm">
                          <span className="w-1.5 h-1.5 bg-white/60 rounded-full" /> {item.duration}
                        </p>

                        {/* SEO Topic Cluster Links */}
                        <div className="flex flex-col gap-2 pointer-events-auto">
                          <Link to={`/itineraries/${item.slug.replace('-dup', '')}`} onClick={handleLinkClick} className="text-sm font-semibold text-[#dce775] hover:text-white transition-colors drop-shadow-md">
                            {item.duration} {item.location.split(',')[0]} itinerary →
                          </Link>
                          <Link to={`/cities/${item.slug.replace('-dup', '')}`} onClick={handleLinkClick} className="text-xs font-medium text-white/90 hover:text-white transition-colors drop-shadow-md">
                            Things to do in {item.location.split(',')[0]}
                          </Link>
                          <Link to={`/blogs/${item.slug.replace('-dup', '')}-travel-guide`} onClick={handleLinkClick} className="text-xs font-medium text-white/90 hover:text-white transition-colors drop-shadow-md">
                            {item.location.split(',')[0]} travel guide
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CONTROLS */}
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute -left-2 md:-left-8 top-1/2 -translate-y-1/2
                     h-10 w-10 md:h-12 md:w-12 rounded-full bg-white/90 backdrop-blur-md shadow-[0_10px_25px_rgba(0,0,0,0.12)]
                     flex items-center justify-center hover:bg-white hover:scale-105 text-[#1f2d1f] transition-all z-30 group"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="opacity-70 group-hover:opacity-100 group-hover:-translate-x-0.5 transition-transform"><polyline points="15 18 9 12 15 6"></polyline></svg>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute -right-2 md:-right-8 top-1/2 -translate-y-1/2
                     h-10 w-10 md:h-12 md:w-12 rounded-full bg-white/90 backdrop-blur-md shadow-[0_10px_25px_rgba(0,0,0,0.12)]
                     flex items-center justify-center hover:bg-white hover:scale-105 text-[#1f2d1f] transition-all z-30 group"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-transform"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </button>
        </div>

        {/* ===== VIEW ALL LINK ===== */}
        <div className="mt-10 flex justify-center md:justify-end md:pr-12">
          <Link
            to="/itineraries"
            aria-label="View all travel itineraries"
            className="text-sm md:text-base font-semibold text-[#5b6f00] hover:text-[#4a5a00] hover:underline underline-offset-4 transition-all"
          >
            View all itineraries →
          </Link>
        </div>
      </div>
    </section>

  );
};

export default ItinerarySlider;
